import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, StatusBadge, FilterChips } from "@/components/app/PageBits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, FileText, Pause, ArrowRightLeft, ExternalLink, Search, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { CYCLE_LABEL, fmtDate } from "@/lib/api";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import CoOwnersEditor from "@/components/app/CoOwnersEditor";

const emptyPlot = { project_id: "", plot_no: "", area_sqft: "", primary_owner_id: "", co_owners: [], maintenance_plan: "regular", billing_cycle: "quarterly", start_date: "" };

export default function Plots() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [searchParams, setSearchParams] = useSearchParams();
  const [plots, setPlots] = useState([]);
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyPlot);
  const [pauseOpen, setPauseOpen] = useState(false);
  const [pauseForm, setPauseForm] = useState({ plot_id: "", start_date: "", end_date: "", reason: "" });
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferForm, setTransferForm] = useState({ plot_id: "", to_customer_id: "", transfer_date: "", notes: "" });

  // Edit / Delete (super_admin)
  const [editOpen, setEditOpen] = useState(false);
  const [editFor, setEditFor] = useState(null);
  const [editForm, setEditForm] = useState({ plot_no: "", area_sqft: "", co_owners: [] });
  const [editBusy, setEditBusy] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [delFor, setDelFor] = useState(null);
  const [delBusy, setDelBusy] = useState(false);

  const load = async () => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (statusFilter !== "all") params.status = statusFilter;
    const [pl, pr, cu] = await Promise.all([
      api.get("/plots", { params }),
      api.get("/projects"),
      api.get("/customers"),
    ]);
    setPlots(pl.data); setProjects(pr.data); setCustomers(cu.data);
  };
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, statusFilter]);
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (q.trim()) next.set("q", q.trim()); else next.delete("q");
    if (statusFilter !== "all") next.set("status", statusFilter); else next.delete("status");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, statusFilter]);

  const submit = async () => {
    try {
      await api.post("/plots", { ...form, area_sqft: Number(form.area_sqft) });
      toast.success("Plot created"); setOpen(false); setForm(emptyPlot); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const genInvoice = async (pid) => {
    try { const r = await api.post(`/plots/${pid}/generate-invoice`); toast.success(`Generated ${r.data.invoice_no}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const submitPause = async () => {
    try { await api.post("/pause-requests", pauseForm); toast.success("Pause requested — awaiting Super Admin"); setPauseOpen(false); }
    catch(e){ toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const submitTransfer = async () => {
    try { await api.post("/plot-transfers", transferForm); toast.success("Transfer recorded"); setTransferOpen(false); load(); }
    catch(e){ toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openEdit = (p) => {
    setEditFor(p);
    // backend stores pan_masked only — surface it as pan hint; blank by default so the user can either leave it or replace
    const coRows = (p.co_owners || []).map((c) => ({
      name: c.name || "",
      phone: c.phone || "",
      email: c.email || "",
      pan: "", // never leak masked back as editable
    }));
    setEditForm({ plot_no: p.plot_no || "", area_sqft: String(p.area_sqft ?? ""), co_owners: coRows });
    setEditOpen(true);
  };
  const submitEdit = async () => {
    setEditBusy(true);
    try {
      const payload = {};
      if (editForm.plot_no && editForm.plot_no !== editFor.plot_no) payload.plot_no = editForm.plot_no;
      const area = Number(editForm.area_sqft);
      if (!Number.isNaN(area) && area > 0 && area !== editFor.area_sqft) payload.area_sqft = area;
      // always send co_owners so remove/add take effect — backend sanitizes
      payload.co_owners = editForm.co_owners || [];
      await api.patch(`/plots/${editFor.id}`, payload);
      toast.success("Plot updated");
      setEditOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setEditBusy(false); }
  };

  const openDelete = (p) => { setDelFor(p); setDelOpen(true); };
  const submitDelete = async () => {
    setDelBusy(true);
    try {
      const r = await api.delete(`/plots/${delFor.id}`);
      toast.success(r.data?.void_invoices_dropped
        ? `Plot deleted. Also removed ${r.data.void_invoices_dropped} void invoice(s).`
        : "Plot deleted");
      setDelOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setDelBusy(false); }
  };

  return (
    <div>
      <PageHeader
        title={statusFilter === "active" ? "Active plots" : statusFilter === "paused" ? "Paused plots" : "Plots"}
        subtitle="Every plot is billed on its own cycle (Monthly / Quarterly / Half-Yearly / Yearly) at the plan rate frozen on the invoice."
        actions={
          <div className="flex gap-2 items-center">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground pointer-events-none"/>
              <Input data-testid="plot-search" placeholder="Search plot / owner / email / phone"
                value={q} onChange={(e)=>setQ(e.target.value)}
                className="pl-8 w-72"/>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="plot-status-filter" className="w-36 max-md:hidden"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="transferred">Transferred</SelectItem>
              </SelectContent>
            </Select>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-plot-btn" className="fab-m"><Plus className="h-4 w-4 mr-2"/>New plot</Button></DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>New plot</DialogTitle></DialogHeader>
              <div className="grid gap-3 grid-cols-2 py-2">
                <div className="col-span-2">
                  <Label>Project</Label>
                  <Select value={form.project_id} onValueChange={(v)=>setForm({...form,project_id:v})}>
                    <SelectTrigger data-testid="plot-project"><SelectValue placeholder="Select project"/></SelectTrigger>
                    <SelectContent>{projects.map(p=><SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Plot no.</Label><Input data-testid="plot-no" value={form.plot_no} onChange={(e)=>setForm({...form,plot_no:e.target.value})}/></div>
                <div><Label>Area (sqft)</Label><Input data-testid="plot-area" type="number" value={form.area_sqft} onChange={(e)=>setForm({...form,area_sqft:e.target.value})}/></div>
                <div className="col-span-2">
                  <Label>Primary owner</Label>
                  <Select value={form.primary_owner_id} onValueChange={(v)=>setForm({...form,primary_owner_id:v})}>
                    <SelectTrigger data-testid="plot-owner"><SelectValue placeholder="Select customer"/></SelectTrigger>
                    <SelectContent>{customers.map(c=><SelectItem key={c.id} value={c.id}>{c.full_name} · {c.email}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Plan</Label>
                  <Select value={form.maintenance_plan} onValueChange={(v)=>setForm({...form,maintenance_plan:v})}>
                    <SelectTrigger data-testid="plot-plan"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="regular">Regular</SelectItem>
                      <SelectItem value="self_managed">Self-Managed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Billing cycle</Label>
                  <Select value={form.billing_cycle} onValueChange={(v)=>setForm({...form,billing_cycle:v})}>
                    <SelectTrigger data-testid="plot-cycle"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="half_yearly">Half-Yearly</SelectItem>
                      <SelectItem value="yearly">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>Start date</Label><Input data-testid="plot-start" type="date" value={form.start_date} onChange={(e)=>setForm({...form,start_date:e.target.value})}/></div>
                <CoOwnersEditor
                  value={form.co_owners}
                  onChange={(v)=>setForm({...form, co_owners: v})}
                  testIdPrefix="new-coowner"/>
              </div>
              <DialogFooter><Button data-testid="plot-save-btn" onClick={submit}>Create plot</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        }
      />
      <FilterChips testId="plot-chips" value={statusFilter} onChange={setStatusFilter} options={[
        { value: "all", label: "All" }, { value: "active", label: "Active" },
        { value: "paused", label: "Paused" }, { value: "transferred", label: "Transferred" },
      ]}/>
      <div className="border border-border rounded-md bg-card overflow-hidden">
        <table className="w-full text-sm m-cards">
          <thead className="bg-muted/60"><tr className="text-left">
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Plot</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Project</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Owner</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider text-right">Area</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Plan</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Cycle</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Next cycle</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Status</th>
            <th className="px-4 py-3 text-right"></th>
          </tr></thead>
          <tbody>
            {plots.map((p) => (
              <tr key={p.id} data-testid={`plot-row-${p.id}`} className="border-t border-border hover:bg-muted/40">
                <td className="px-4 py-3 font-medium">
                  <Link to={`/plots/${p.id}`} data-testid={`plot-link-${p.id}`} className="inline-flex items-center gap-1 hover:text-primary transition-colors">
                    {p.plot_no} <ExternalLink className="h-3 w-3 opacity-60"/>
                  </Link>
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  <span className="inline-flex items-center gap-2">
                    {(() => {
                      const proj = projects.find(pr => pr.id === p.project_id);
                      return proj?.logo_file_id ? (
                        <img src={`${api.defaults.baseURL}/projects/${proj.id}/logo?v=${proj.logo_updated_at||''}`}
                          alt="" className="h-6 w-6 rounded object-contain border border-border bg-white"/>
                      ) : null;
                    })()}
                    <span>{p.project_name}</span>
                  </span>
                </td>
                <td className="px-4 py-3">{p.primary_owner_name}</td>
                <td className="px-4 py-3 text-right tabular">{p.area_sqft.toLocaleString("en-IN")}</td>
                <td className="px-4 py-3 capitalize">{p.maintenance_plan.replace("_"," ")}</td>
                <td className="px-4 py-3">{CYCLE_LABEL[p.billing_cycle]}</td>
                <td className="px-4 py-3 tabular text-muted-foreground">{fmtDate(p.next_cycle_start)}</td>
                <td className="px-4 py-3"><StatusBadge status={p.status}/></td>
                <td className="px-4 py-3 flex gap-1 justify-end">
                  <Button data-testid={`gen-inv-${p.id}`} size="sm" variant="outline" onClick={()=>genInvoice(p.id)}><FileText className="h-3 w-3 mr-1"/>Invoice</Button>
                  <Button data-testid={`pause-${p.id}`} size="sm" variant="outline" onClick={()=>{setPauseForm({plot_id:p.id,start_date:"",end_date:"",reason:""});setPauseOpen(true);}}><Pause className="h-3 w-3"/></Button>
                  <Button data-testid={`transfer-${p.id}`} size="sm" variant="outline" onClick={()=>{setTransferForm({plot_id:p.id,to_customer_id:"",transfer_date:"",notes:""});setTransferOpen(true);}}><ArrowRightLeft className="h-3 w-3"/></Button>
                  {isSuper && (
                    <>
                      <Button data-testid={`plot-edit-${p.id}`} size="icon" variant="ghost"
                        onClick={()=>openEdit(p)} title="Edit plot metadata">
                        <Pencil className="h-3.5 w-3.5"/>
                      </Button>
                      <Button data-testid={`plot-delete-${p.id}`} size="icon" variant="ghost"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={()=>openDelete(p)} title="Delete plot">
                        <Trash2 className="h-3.5 w-3.5"/>
                      </Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {plots.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">No plots yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={pauseOpen} onOpenChange={setPauseOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Request pause / free period</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-2">
            <div><Label>Start date</Label><Input data-testid="pause-start" type="date" value={pauseForm.start_date} onChange={(e)=>setPauseForm({...pauseForm,start_date:e.target.value})}/></div>
            <div><Label>End date</Label><Input data-testid="pause-end" type="date" value={pauseForm.end_date} onChange={(e)=>setPauseForm({...pauseForm,end_date:e.target.value})}/></div>
            <div><Label>Reason</Label><Input data-testid="pause-reason" value={pauseForm.reason} onChange={(e)=>setPauseForm({...pauseForm,reason:e.target.value})}/></div>
          </div>
          <DialogFooter><Button data-testid="pause-submit" onClick={submitPause}>Request</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Transfer plot</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-2">
            <div>
              <Label>Transfer to</Label>
              <Select value={transferForm.to_customer_id} onValueChange={(v)=>setTransferForm({...transferForm,to_customer_id:v})}>
                <SelectTrigger data-testid="transfer-to"><SelectValue placeholder="Select customer"/></SelectTrigger>
                <SelectContent>{customers.map(c=><SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Transfer date</Label><Input data-testid="transfer-date" type="date" value={transferForm.transfer_date} onChange={(e)=>setTransferForm({...transferForm,transfer_date:e.target.value})}/></div>
            <div><Label>Notes</Label><Input data-testid="transfer-notes" value={transferForm.notes} onChange={(e)=>setTransferForm({...transferForm,notes:e.target.value})}/></div>
          </div>
          <DialogFooter><Button data-testid="transfer-submit" onClick={submitTransfer}>Transfer</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit plot (metadata-only) */}
      <Dialog open={editOpen} onOpenChange={(v)=>{ if (!editBusy) setEditOpen(v); }}>
        <DialogContent data-testid="plot-edit-dialog" className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Edit plot · {editFor?.plot_no}</DialogTitle>
            <DialogDescription>
              Only safe metadata can be edited here. To change plan, cycle, or owner use the dedicated flows (Plan change / Transfer).
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 grid-cols-2 py-2">
            <div><Label>Plot no.</Label>
              <Input data-testid="plot-edit-no" value={editForm.plot_no}
                onChange={(e)=>setEditForm({...editForm, plot_no: e.target.value})}/>
            </div>
            <div><Label>Area (sqft)</Label>
              <Input data-testid="plot-edit-area" type="number" step="0.01" value={editForm.area_sqft}
                onChange={(e)=>setEditForm({...editForm, area_sqft: e.target.value})}/>
            </div>
            <div className="col-span-2 text-xs text-muted-foreground -mt-1">
              Changing area affects future invoices only — invoices already generated store the area snapshot at the time of billing.
            </div>
            <CoOwnersEditor
              value={editForm.co_owners}
              onChange={(v)=>setEditForm({...editForm, co_owners: v})}
              testIdPrefix="edit-coowner"/>
            <div className="col-span-2 text-xs text-muted-foreground -mt-1">
              PAN shows as masked once saved. Leave PAN blank to keep the existing value for that co-owner, or re-enter a new PAN to overwrite.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setEditOpen(false)} disabled={editBusy}>Cancel</Button>
            <Button data-testid="plot-edit-submit" onClick={submitEdit} disabled={editBusy}>
              {editBusy ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete plot */}
      <Dialog open={delOpen} onOpenChange={(v)=>{ if (!delBusy) setDelOpen(v); }}>
        <DialogContent data-testid="plot-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete plot · {delFor?.plot_no}?</DialogTitle>
            <DialogDescription>
              This permanently removes the plot and any <strong>void</strong> invoices tied to it.
              If the plot still has non-void invoices, the delete will be blocked — void every invoice first (and refund payments) before deleting.
              <span className="block mt-2">The customer and their other plots are not affected.</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setDelOpen(false)} disabled={delBusy}>Cancel</Button>
            <Button data-testid="plot-delete-confirm" variant="destructive" onClick={submitDelete} disabled={delBusy}>
              {delBusy ? "Deleting…" : "Delete plot"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
